import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Award, ScrollText } from 'lucide-react';
import { API, DOC_TYPES, useCustomDocumentTypes } from './docTypesShared';

export default function useUploadDocumentModal({ membre, open, token, onDocumentUploaded }) {
  const [typeDocument, setTypeDocument] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // V36 — Date d'origine (antériorité avant le 01/05/2026). Auto-détectée
  // depuis le nom de fichier ou laissée à vide si actuelle.
  const [dateOrigine, setDateOrigine] = useState('');
  const [dateOrigineAuto, setDateOrigineAuto] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Filtre par catégorie pour la liste "Documents insérés".
  // 'all' = afficher tous, sinon nom de la catégorie issue de DOC_TYPES + customTypes.
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Recherche textuelle (insensible à la casse + accents) sur original_filename
  // + label du type de document. Combinée au filtre catégorie.
  const [searchQuery, setSearchQuery] = useState('');
  // Export ZIP en cours (bouton désactivé pendant)
  const [exportingZip, setExportingZip] = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!membre?.id || !token) return;
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API}/api/admin/membres/${membre.id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data.documents || []);
    } catch {
      // Silently handle
    } finally {
      setLoadingDocs(false);
    }
  }, [membre?.id, token]);

  useEffect(() => {
    if (open && membre?.id) {
      setTypeDocument('');
      setDescription('');
      setFile(null);
      fetchDocuments();
    }
  }, [open, membre?.id, fetchDocuments]);

  // V36 — Détection automatique d'une date d'antériorité dans le nom du fichier.
  // Cherche : 2024-05-12, 12-05-2024, 12_05_2024, 12/05/2024, 2024_05, MAI-2024,
  // ou simplement "2024" / "2023" (année seule). Retourne une string ISO YYYY-MM-DD.
  const _detectDateInFilename = (filename) => {
    if (!filename) return '';
    const name = filename.toLowerCase().replace(/\.[a-z0-9]{2,5}$/, '');
    // Patterns ISO et numériques
    const patterns = [
      // 2024-05-12, 2024_05_12, 2024.05.12, 2024/05/12
      /\b(20\d{2}|19\d{2})[-_./](0?[1-9]|1[0-2])[-_./](0?[1-9]|[12]\d|3[01])\b/,
      // 12-05-2024, 12_05_2024, 12.05.2024, 12/05/2024
      /\b(0?[1-9]|[12]\d|3[01])[-_./](0?[1-9]|1[0-2])[-_./](20\d{2}|19\d{2})\b/,
      // 2024-05, 2024_05, 2024.05
      /\b(20\d{2}|19\d{2})[-_./](0?[1-9]|1[0-2])\b/,
      // 2024 seul
      /\b(20[0-1]\d|19\d{2})\b/,
    ];
    for (const re of patterns) {
      const m = name.match(re);
      if (!m) continue;
      let y, mo, d;
      if (m[0].length > 9 && /^20|^19/.test(m[1])) { // YYYY-MM-DD
        [, y, mo, d] = m;
      } else if (m[0].length > 9) { // DD-MM-YYYY
        [, d, mo, y] = m;
      } else if (m.length === 3) { // YYYY-MM
        [, y, mo] = m; d = '01';
      } else {
        y = m[1]; mo = '01'; d = '01';
      }
      const yi = parseInt(y, 10);
      if (yi < 1990 || yi > new Date().getFullYear() + 1) continue;
      const iso = `${y}-${String(mo).padStart(2, '0')}-${String(d || 1).padStart(2, '0')}`;
      // Sanity check
      const dt = new Date(iso);
      if (!isNaN(dt.getTime())) return iso;
    }
    return '';
  };

  const _applyAutoDate = (selectedFile) => {
    if (!selectedFile) return;
    const detected = _detectDateInFilename(selectedFile.name);
    if (detected && !dateOrigine) {
      setDateOrigine(detected);
      setDateOrigineAuto(true);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const allowed = ['application/pdf', 'image/png', 'image/jpeg'];
      if (!allowed.includes(droppedFile.type)) {
        toast.error('Seuls les fichiers PDF, PNG et JPEG sont acceptes');
        return;
      }
      setFile(droppedFile);
      _applyAutoDate(droppedFile);
    }
  }, [dateOrigine]); // eslint-disable-line

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      _applyAutoDate(selected);
    }
  };

  const handleUpload = async () => {
    if (!file || !typeDocument) {
      toast.error('Selectionnez un type de document et un fichier');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type_document', typeDocument);
      if (description) formData.append('description', description);
      if (dateOrigine) {
        formData.append('date_origine', dateOrigine);
        formData.append('date_origine_auto', String(!!dateOrigineAuto));
      }

      await axios.post(
        `${API}/api/admin/membres/${membre.id}/documents/upload`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document insere avec succes');
      setFile(null);
      setTypeDocument('');
      setDescription('');
      setDateOrigine('');
      setDateOrigineAuto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
      if (onDocumentUploaded) onDocumentUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'insertion du document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    setDeletingId(docId);
    try {
      await axios.delete(`${API}/api/admin/membres/${membre.id}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprime');
      fetchDocuments();
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const fetchBlob = async (docId, mode) => {
    const res = await fetch(`${API}/api/admin/membres/${membre.id}/documents/${docId}/download?mode=${mode}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  };

  const handleView = async (doc) => {
    try {
      const blob = await fetchBlob(doc.id, 'view');
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Erreur lors de la visualisation');
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const blob = await fetchBlob(doc.id, 'download');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du telechargement');
    }
  };

  const handlePrintDoc = async (doc) => {
    try {
      const blob = await fetchBlob(doc.id, 'view');
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;opacity:0;';
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
        catch { window.open(url, '_blank'); }
        setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 1000);
      };
      iframe.src = url;
    } catch {
      toast.error('Erreur lors de l\'impression');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  };

  // Helpers de filtre — utilisés par l'export ZIP pour respecter les filtres actifs
  const _normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const computeVisibleDocuments = () => {
    const q = _normalize(searchQuery.trim());
    const searched = q
      ? documents.filter((doc) => {
          const conf = [...ALL_DOC_TYPES, ...customTypes].find(d => d.value === doc.type_document);
          const haystack = _normalize(`${doc.original_filename || ''} ${(conf && conf.label) || ''} ${doc.type_document || ''}`);
          return haystack.includes(q);
        })
      : documents;
    return categoryFilter === 'all'
      ? searched
      : searched.filter(doc => {
          const conf = [...ALL_DOC_TYPES, ...customTypes].find(d => d.value === doc.type_document);
          return ((conf && conf.category) || 'Autre') === categoryFilter;
        });
  };

  const handleExportZip = async () => {
    const visible = computeVisibleDocuments();
    if (visible.length === 0) {
      toast.error('Aucun document à exporter');
      return;
    }
    setExportingZip(true);
    try {
      // Si filtre actif (recherche ou catégorie), passer la liste d'IDs au backend
      const isFiltered = searchQuery.trim() !== '' || categoryFilter !== 'all';
      const params = isFiltered
        ? { ids: visible.map(d => d.id).join(',') }
        : {};
      const res = await axios.get(
        `${API}/api/admin/membres/${membre.id}/documents/export-zip`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
          responseType: 'blob',
        },
      );
      // Forcer le download
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename depuis le header Content-Disposition (sinon fallback)
      const dispo = res.headers?.['content-disposition'] || '';
      const m = dispo.match(/filename="?([^";]+)"?/i);
      a.download = m ? m[1] : `Documents_${membre.prenom || ''}_${membre.nom || 'membre'}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const suffix = isFiltered ? ` (${visible.length}/${documents.length} filtrés)` : '';
      toast.success(`ZIP téléchargé${suffix}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'export ZIP');
    } finally {
      setExportingZip(false);
    }
  };

  const ALL_DOC_TYPES = [
    ...DOC_TYPES,
    { value: 'certificat_adhesion', label: 'Certificat O\'SCOP', color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: ShieldCheck },
    { value: 'attestation_ftpe_generee', label: 'Attestation FTPE', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Award },
    { value: 'devis_ess_genere', label: 'Devis ESS', color: 'bg-violet-100 text-violet-700 border-violet-300', icon: ScrollText },
  ];
  // Types personnalisés ajoutés par le super admin (mergés avec DOC_TYPES dans
  // le Select ci-dessous pour le picker, et avec ALL_DOC_TYPES pour le badge).
  const customTypes = useCustomDocumentTypes(token);
  const PICKER_TYPES = [...DOC_TYPES, ...customTypes];
  const getTypeConfig = (type) =>
    [...ALL_DOC_TYPES, ...customTypes].find(d => d.value === type) || DOC_TYPES[0];

  return {
    ALL_DOC_TYPES, PICKER_TYPES, _applyAutoDate, _detectDateInFilename, _normalize, categoryFilter,
    computeVisibleDocuments, customTypes, dateOrigine, dateOrigineAuto, deletingId, description,
    documents, dragOver, exportingZip, fetchBlob, fetchDocuments, file,
    fileInputRef, formatSize, getTypeConfig, handleDelete, handleDownloadDoc, handleDrop,
    handleExportZip, handleFileChange, handlePrintDoc, handleUpload, handleView, loadingDocs,
    searchQuery, setCategoryFilter, setDateOrigine, setDateOrigineAuto, setDeletingId, setDescription,
    setDocuments, setDragOver, setExportingZip, setFile, setLoadingDocs, setSearchQuery,
    setTypeDocument, setUploading, typeDocument, uploading,
  };
}
